import React from "react";
import { daysLate, dayCount, isLowStock, stockReason } from "../../utils/attention";

// SGEW 18 Oct 2026: the Items page's pill on a value, the same on every list - red when it needs attention (stock at or
// below its minimum, late, waiting too long), green when it is fine (the Items page's "enough stock"), amber while
// something open is still in time, grey when there is nothing to do. `title` says why (hover); keep it short and plain:
// "Below minimum 10", "Late by 4 days", "Waiting 5 days". `compact` = 8px a side (a date in a narrow column). The rules
// are in utils/attention.
const TONES = {
  alert: "bg-[#faebe9] text-[#D0412E]",
  ok: "bg-[#d8fff8] text-[#01bd9b]",
  wait: "bg-amber-100 text-amber-700",
  neutral: "bg-gray-200 text-gray-600",
};

const StatusPill = ({ tone = "neutral", title, compact = false, className = "", children }) => (
  <div
    title={title || undefined}
    data-tone={tone}
    className={`inline-block ${compact ? "px-2" : "px-4"} py-1 rounded-[0.42vw] ${TONES[tone] || TONES.neutral}${className ? ` ${className}` : ""}`}
  >
    {children}
  </div>
);

/** A stock figure: red at or below its minimum (or, without a minimum, at 0), green above it. */
export const StockPill = ({ stock, min, className, children }) => {
  const low = isLowStock(stock, min);
  return (
    <StatusPill tone={low ? "alert" : "ok"} title={stockReason(stock, min)} className={className}>
      {children ?? stock ?? 0}
    </StatusPill>
  );
};

/**
 * A due date: red once it has passed while the work is open ("Late by 4 days"), green while it is open and in time
 * (fine = "ok", or pass inTime="none" for the date as plain text); a closed one is the plain date. `note` adds to the
 * title (e.g. "still waiting for approval").
 */
export const DuePill = ({ due, open, label, inTime = "ok", note = "", compact = false, className = "whitespace-nowrap" }) => {
  const late = open ? daysLate(due) : 0;
  const extra = note ? ` (${note})` : "";
  if (late > 0) {
    return (
      <StatusPill tone="alert" title={`Late by ${dayCount(late)}${extra}`} compact={compact} className={className}>
        {label}
      </StatusPill>
    );
  }
  if (open && due && inTime !== "none") {
    return (
      <StatusPill tone={inTime} title={`Due ${label}${extra}`} compact={compact} className={className}>
        {label}
      </StatusPill>
    );
  }
  return <span>{label}</span>;
};

export default StatusPill;
